import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import './TennisAnalyticsVisual.css';

/* ── Animated counter hook ── */
// eslint-disable-next-line no-unused-vars
const useCountUp = (end, duration = 1.6, startDelay = 0.6) => {
  const [count, setCount] = useState(0);
  const rafRef = useRef(null);

  useEffect(() => {
    const timeout = setTimeout(() => {
      const start = performance.now();
      const step = (now) => {
        const elapsed = Math.min((now - start) / (duration * 1000), 1);
        // Ease-out cubic
        const eased = 1 - Math.pow(1 - elapsed, 3);
        setCount(Math.round(eased * end));
        if (elapsed < 1) rafRef.current = requestAnimationFrame(step);
      };
      rafRef.current = requestAnimationFrame(step);
    }, startDelay * 1000);
    return () => {
      clearTimeout(timeout);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [end, duration, startDelay]);

  return count;
};

const TennisAnalyticsVisual = () => {
  const [featureIndex, setFeatureIndex] = useState(0);
  const { t } = useTranslation();

  const featureKeys = ['hero.chip_court', 'hero.chip_analytics', 'hero.chip_detection'];

  // Cycle through feature labels every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setFeatureIndex((prev) => (prev + 1) % featureKeys.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [featureKeys.length]);

  const scaleIn = (delay = 0) => ({
    hidden: { opacity: 0, scale: 0.85 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }
    }
  });

  return (
    <div className="hero-right-modern">
      {/* soft radial accents */}
      <div className="accent-ring" aria-hidden="true" />
      <div className="accent-ring ring-2" aria-hidden="true" />

      {/* Headline — static, no opacity:0 — LCP candidate must paint immediately */}
      <h1 className="hrm-title">
        {t('hero.headline_prefix')}<br />
        <span className="text-primary-neon">{t('hero.headline_highlight')}</span>
      </h1>

      {/* Value Prop — static, no opacity:0 — this IS the LCP element */}
      <p className="hrm-subtitle">
        {t('hero.subtitle_text')}
      </p>

      {/* Feature rotator — subtle scale-in, not LCP-critical */}
      <motion.div
        className="hrm-feature-rotator"
        variants={scaleIn(0.3)}
        initial="hidden"
        animate="visible"
      >
        <div className="rotator-corner-frame">
          <AnimatePresence mode="wait">
            <motion.span
              key={featureIndex}
              className="rotator-label"
              initial={{ opacity: 0, y: 14, filter: 'blur(4px)' }}
              animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              exit={{ opacity: 0, y: -14, filter: 'blur(4px)' }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            >
              {t(featureKeys[featureIndex])}
            </motion.span>
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};

export default TennisAnalyticsVisual;